import { useEffect, useMemo, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  FileSpreadsheet,
  Layers,
  Printer,
  Search,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { EventsSubTabs } from '@/components/events/EventsSubTabs'
import { DISCIPLINE_LABELS } from '@/components/events/EventFormModal'
import {
  useEventLocations,
  useEvents,
  useRecommendations,
  type RecommendationItem,
} from '@/hooks/useEvents'
import { useEventsStore } from '@/store/eventsStore'
import { apiFetchBlob } from '@/lib/api/rawFetch'
import { fmtDate, todayIso } from '@/lib/dates'
import { SELL_THROUGH_TEXT, sellThroughLevel } from '@/lib/sellThrough'

// View B — Pack List Builder (spec §View B). Gina picks location + event week,
// reviews discipline-aware recommendations, adjusts quantities, exports the
// pull sheet for staff. Table styled after the Dashboard product grid
// (dash-table classes, sticky sortable headers, striped rows) with a filter
// toolbar. Selection persists in eventsStore across page navigation.

type RowState = { include: boolean; final_qty: string }

const DISCIPLINE_TAG_LABELS: Record<string, string> = {
  hunter_jumper: 'HJ',
  dressage: 'Dressage',
}

const STATUS_LABELS: Record<string, string> = {
  HISTORY: 'History',
  CROSS_DISCIPLINE: 'Cross-discipline',
  NEW: 'New arrival',
}

type SortKey =
  | 'status'
  | 'discipline_tag'
  | 'product'
  | 'variant'
  | 'last_event'
  | 'at_venue'
  | 'margin_pct'
  | 'recommended_qty'
  | 'brewster_stock'
  | 'stock_ok'
  | 'online_risk'
  | 'final_qty'

// Numeric/flag columns sort high→low on first click; text columns A→Z.
const NUMERIC_SORT = new Set<SortKey>([
  'last_event', 'at_venue', 'margin_pct', 'recommended_qty', 'brewster_stock',
  'stock_ok', 'online_risk', 'final_qty',
])

type Column = {
  key: SortKey | 'include'
  label: string
  width: number
  numeric?: boolean
  sortable?: boolean
  tooltip?: string
}

const COLUMNS: Column[] = [
  { key: 'include', label: '', width: 36 },
  { key: 'status', label: 'Status', width: 150, sortable: true, tooltip: 'History = sold here before at this discipline. Cross-discipline = history from the other discipline. New arrival = added to Shopify in the last 90 days, no history here.' },
  { key: 'discipline_tag', label: 'Disc.', width: 80, sortable: true, tooltip: 'Product discipline tag from Shopify (Hunter-Jumper / Dressage). "All" = no tag — sells to everyone.' },
  { key: 'product', label: 'Brand + Product', width: 300, sortable: true, tooltip: 'One row per product — click to open its variants and sizes.' },
  { key: 'variant', label: 'Variants', width: 130, sortable: true },
  { key: 'last_event', label: 'Last Event (avail / sold / ST%)', width: 215, sortable: true, tooltip: 'Most recent event at this location + discipline: units available (carryover + transferred) / units sold · sell-through %. ⚠ = stocked out.' },
  { key: 'at_venue', label: 'At venue', width: 90, numeric: true, sortable: true, tooltip: 'Estimated units already at the venue — leftover after the last synced event, adjusted for transfers since. Already subtracted from Rec. Qty.' },
  { key: 'margin_pct', label: 'Margin %', width: 90, numeric: true, sortable: true },
  { key: 'recommended_qty', label: 'Rec. Qty', width: 90, numeric: true, sortable: true, tooltip: 'Suggested quantity from the sell-through band multipliers. Hover a product name for the reason.' },
  { key: 'brewster_stock', label: 'Brewster', width: 90, numeric: true, sortable: true, tooltip: 'Units currently available at the Brewster store.' },
  { key: 'stock_ok', label: 'Stock OK', width: 80, sortable: true, tooltip: '✓ = Brewster stock covers the recommended quantity. Sort to surface shortfalls.' },
  { key: 'online_risk', label: 'Online Risk', width: 90, sortable: true, tooltip: '⚠ = pulling the final qty leaves Brewster under 2 weeks of online sales for this variant. Sort to surface risks.' },
  { key: 'final_qty', label: 'Final Qty', width: 90, numeric: true, sortable: true },
]

// Floor for drag-resized columns; the 36px checkbox column is excluded from
// resizing entirely and keeps its fixed width.
const MIN_COL_WIDTH = 48

// Shared status-pill base (colour pass): filled, sentence case, no border.
const PILL_BASE =
  'inline-block rounded-full text-[10.5px] font-semibold px-2 py-0.5 leading-none whitespace-nowrap'

// Amber (awaiting) = check this before packing: "New" means the qty is a
// guess with no history, "Low stock" means Brewster can't cover it. Neutral
// (archived) = context only: cross-discipline history and the discipline tag.
function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'attention' | 'neutral' }) {
  const cls =
    tone === 'attention'
      ? 'bg-[var(--status-awaiting-bg)] text-[var(--status-awaiting-fg)]'
      : 'bg-[var(--status-archived-bg)] text-[var(--status-archived-fg)]'
  return <span className={`${PILL_BASE} ${cls}`}>{children}</span>
}

/** Review flag for recommendations that include hand-typed offline units —
 * those are less reliable than POS, so Gina confirms before buying. Styled as
 * an amber (awaiting) review pill. Renders nothing when n is 0. */
export function OfflineQtyPill({ n }: { n: number }) {
  if (n <= 0) return null
  return (
    <span
      className={`ml-1 ${PILL_BASE} bg-[var(--status-awaiting-bg)] text-[var(--status-awaiting-fg)]`}
      title={`Includes ${n} units sold offline (from warehouse). Offline sales are entered by hand — confirm before buying.`}
      data-testid="offline-qty-pill"
    >
      incl. {n} offline — check
    </span>
  )
}

function SortIcon({ sorted }: { sorted: false | 'asc' | 'desc' }) {
  if (sorted === 'asc') return <ChevronUp className="inline h-3 w-3 ml-0.5 opacity-60" />
  if (sorted === 'desc') return <ChevronDown className="inline h-3 w-3 ml-0.5 opacity-60" />
  return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 opacity-30" />
}

function finalQtyOf(state: RowState | undefined): number {
  if (!state) return 0
  const n = Math.trunc(Number(state.final_qty))
  return Number.isFinite(n) && n > 0 ? n : 0
}

export default function EventsPackList() {
  const { data: locations = [] } = useEventLocations()
  const { data: allEvents = [] } = useEvents()

  const {
    packLocationId: locationId,
    packEventId: eventId,
    packExtraIds: extraIds,
    packDiscipline: discipline,
    setPackSelection,
    packColWidths,
    setPackColWidth,
    resetPackColWidth,
  } = useEventsStore()
  const [rows, setRows] = useState<Map<number, RowState>>(new Map())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [exporting, setExporting] = useState<'xlsx' | 'html' | null>(null)

  // Filter toolbar state (view-only — doesn't change what exports).
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [vendorFilter, setVendorFilter] = useState('')
  const [flagFilter, setFlagFilter] = useState('')

  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  // Event weeks at this location, upcoming first (soonest → latest), then past
  // weeks most-recent first — pack lists are built for upcoming events.
  const locationEvents = useMemo(() => {
    const today = todayIso()
    const mine = allEvents.filter((e) => e.shopify_location_id === locationId)
    const upcoming = mine
      .filter((e) => e.event_date >= today)
      .sort((a, b) => a.event_date.localeCompare(b.event_date))
    const past = mine
      .filter((e) => e.event_date < today)
      .sort((a, b) => b.event_date.localeCompare(a.event_date))
    return [...upcoming, ...past]
  }, [allEvents, locationId])

  const selectedEvent = locationEvents.find((e) => e.id === eventId)

  // All weeks being packed together; the EARLIEST drives the history
  // cutoff (no selected week's own sales feed its recommendations) and
  // receives the exports.
  const packedEvents = useMemo(() => {
    const ids = new Set([eventId, ...extraIds])
    return locationEvents
      .filter((e) => ids.has(e.id))
      .sort((a, b) => a.event_date.localeCompare(b.event_date))
  }, [locationEvents, eventId, extraIds])
  const earliestEvent = packedEvents[0]

  // Persisted ids may point at deleted events — clear them once events load.
  useEffect(() => {
    if (allEvents.length === 0 || locationId == null) return
    if (eventId != null && selectedEvent == null) {
      setPackSelection({ packEventId: undefined, packExtraIds: [], packDiscipline: '' })
      return
    }
    const valid = extraIds.filter((id) => locationEvents.some((e) => e.id === id))
    if (valid.length !== extraIds.length) {
      setPackSelection({ packExtraIds: valid })
    }
  }, [allEvents, eventId, locationId, selectedEvent, extraIds, locationEvents, setPackSelection])

  function onSelectLocation(id: number | undefined) {
    setPackSelection({ packLocationId: id, packEventId: undefined, packExtraIds: [], packDiscipline: '' })
  }

  function onSelectWeek(id: number | undefined) {
    const e = locationEvents.find((ev) => ev.id === id)
    // Discipline auto-fills from the registered week — stays editable.
    setPackSelection({ packEventId: id, packExtraIds: [], packDiscipline: e ? e.discipline : '' })
  }

  const { data: recs, isLoading } = useRecommendations(locationId, {
    discipline: discipline || undefined,
    event_date: earliestEvent?.event_date,
  })

  // Reset include/final-qty state whenever a new recommendation set loads.
  useEffect(() => {
    if (!recs) return
    const next = new Map<number, RowState>()
    for (const it of recs.items) {
      next.set(it.variant_id, { include: true, final_qty: String(it.recommended_qty) })
    }
    setRows(next)
    setExpanded(new Set())
  }, [recs])

  function patchRow(variantId: number, patch: Partial<RowState>) {
    setRows((prev) => {
      const next = new Map(prev)
      const cur = next.get(variantId) ?? { include: true, final_qty: '0' }
      next.set(variantId, { ...cur, ...patch })
      return next
    })
  }

  function toggleExpanded(groupKey: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }

  function onSort(key: SortKey) {
    // Numbers/flags: high→low first. Text: A→Z first. Third click resets.
    const first = NUMERIC_SORT.has(key) ? 'desc' : 'asc'
    if (sortKey !== key) {
      setSortKey(key)
      setSortDir(first)
    } else if (sortDir === first) {
      setSortDir(first === 'desc' ? 'asc' : 'desc')
    } else {
      setSortKey(null)
    }
  }

  // Online risk recomputed against Gina's CURRENT final qty, not the server's
  // recommended qty (spec §Online-Channel Stockout Guard): remaining < 2 weeks
  // of online velocity. Taking nothing can't put the website at risk, and zero
  // online velocity means there's no channel to protect.
  function onlineRiskOf(it: RecommendationItem): boolean {
    const finalQty = finalQtyOf(rows.get(it.variant_id))
    return (
      finalQty > 0 &&
      it.online_weekly_velocity > 0 &&
      it.brewster_stock - finalQty < 2 * it.online_weekly_velocity
    )
  }

  // Facets for the filter dropdowns, from the full unfiltered set.
  const facets = useMemo(() => {
    const types = new Set<string>()
    const vendors = new Set<string>()
    for (const it of recs?.items ?? []) {
      if (it.product_type) types.add(it.product_type)
      if (it.vendor) vendors.add(it.vendor)
    }
    return {
      types: [...types].sort(),
      vendors: [...vendors].sort(),
    }
  }, [recs])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (recs?.items ?? []).filter((it) => {
      if (statusFilter && it.status !== statusFilter) return false
      if (typeFilter && it.product_type !== typeFilter) return false
      if (vendorFilter && it.vendor !== vendorFilter) return false
      if (flagFilter === 'low_stock' && it.stock_ok) return false
      if (flagFilter === 'online_risk' && !onlineRiskOf(it)) return false
      if (flagFilter === 'stockout' && !it.last_stockout) return false
      if (q) {
        const hay = `${it.product_title ?? ''} ${it.variant_title ?? ''} ${it.sku ?? ''} ${it.vendor ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recs, search, statusFilter, typeFilter, vendorFilter, flagFilter, rows])

  // One row per PRODUCT — variants live behind a click so Gina reviews a
  // few hundred products, not a thousand variant rows. Static aggregates
  // here; include/final-qty-dependent ones come from groupDynamics().
  type ProductGroup = {
    key: string
    product_title: string
    vendor: string | null
    discipline_tag: string | null
    statuses: RecommendationItem['status'][]
    margin_pct: number | null
    rec_sum: number
    offline_sum: number
    at_venue_sum: number
    brewster_sum: number
    all_stock_ok: boolean
    last: { avail: number; sold: number; st: number | null; stockout: boolean; date: string } | null
    size_curve: RecommendationItem['size_curve']
    items: RecommendationItem[]
  }

  const groups = useMemo<ProductGroup[]>(() => {
    const byProduct = new Map<string, RecommendationItem[]>()
    for (const it of filtered) {
      const key = it.product_id != null ? `p:${it.product_id}` : `t:${it.product_title ?? it.sku ?? it.variant_id}`
      const list = byProduct.get(key) ?? []
      list.push(it)
      byProduct.set(key, list)
    }
    return [...byProduct.entries()].map(([key, list]) => {
      const statuses = [...new Set(list.map((it) => it.status))]
      const withHistory = list.filter((it) => it.last_event_date != null)
      const lastAvail = withHistory.reduce((s, it) => s + (it.last_qty_available ?? it.last_qty_transferred ?? 0), 0)
      const lastSold = withHistory.reduce((s, it) => s + (it.last_qty_sold ?? 0), 0)
      return {
        key,
        product_title: list[0].product_title ?? list[0].sku ?? `#${list[0].variant_id}`,
        vendor: list[0].vendor,
        discipline_tag: list[0].discipline_tag,
        statuses,
        margin_pct: list.find((it) => it.margin_pct != null)?.margin_pct ?? null,
        rec_sum: list.reduce((s, it) => s + it.recommended_qty, 0),
        offline_sum: list.reduce((s, it) => s + it.offline_qty, 0),
        at_venue_sum: list.reduce((s, it) => s + it.est_at_venue, 0),
        brewster_sum: list.reduce((s, it) => s + it.brewster_stock, 0),
        all_stock_ok: list.every((it) => it.stock_ok),
        last: withHistory.length === 0 ? null : {
          avail: lastAvail,
          sold: lastSold,
          st: lastAvail > 0 ? (lastSold / lastAvail) * 100 : null,
          stockout: withHistory.some((it) => it.last_stockout),
          date: withHistory.reduce((d, it) => (it.last_event_date! > d ? it.last_event_date! : d), ''),
        },
        size_curve: list.find((it) => it.size_curve.length > 0)?.size_curve ?? [],
        items: list,
      }
    })
  }, [filtered])

  // Aggregates that move as Gina edits: total final qty, include state,
  // any-variant online risk.
  function groupDynamics(g: ProductGroup) {
    let finalSum = 0
    let included = 0
    let anyRisk = false
    for (const it of g.items) {
      const st = rows.get(it.variant_id)
      if (st?.include) {
        included++
        finalSum += finalQtyOf(st)
      }
      if (onlineRiskOf(it)) anyRisk = true
    }
    return {
      finalSum,
      anyRisk,
      allIncluded: included === g.items.length,
      noneIncluded: included === 0,
    }
  }

  const sortedGroups = useMemo(() => {
    if (!sortKey) return groups
    const dir = sortDir === 'desc' ? -1 : 1
    const statusOrder = (g: ProductGroup) =>
      Math.min(...g.statuses.map((s) => ({ HISTORY: 0, CROSS_DISCIPLINE: 1, NEW: 2 }[s])))
    const val = (g: ProductGroup): string | number => {
      const dyn = () => groupDynamics(g)
      switch (sortKey) {
        case 'status': return statusOrder(g)
        case 'discipline_tag': return DISCIPLINE_TAG_LABELS[g.discipline_tag ?? ''] ?? 'All'
        case 'product': return `${g.vendor ?? ''} ${g.product_title}`.trim().toLowerCase()
        case 'variant': return g.items.length
        case 'last_event': return g.last?.st ?? -1
        case 'at_venue': return g.at_venue_sum
        case 'margin_pct': return g.margin_pct ?? -1
        case 'recommended_qty': return g.rec_sum
        case 'brewster_stock': return g.brewster_sum
        case 'stock_ok': return g.all_stock_ok ? 0 : 1 // shortfalls (⚠) first on desc
        case 'online_risk': return dyn().anyRisk ? 1 : 0
        case 'final_qty': return dyn().finalSum
      }
    }
    return [...groups].sort((a, b) => {
      const av = val(a)
      const bv = val(b)
      if (typeof av === 'string' || typeof bv === 'string') {
        return dir * String(av).localeCompare(String(bv))
      }
      return dir * (av - bv)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, sortKey, sortDir, rows])

  function patchGroup(g: ProductGroup, include: boolean) {
    setRows((prev) => {
      const next = new Map(prev)
      for (const it of g.items) {
        const cur = next.get(it.variant_id) ?? { include: true, final_qty: String(it.recommended_qty) }
        next.set(it.variant_id, { ...cur, include })
      }
      return next
    })
  }

  // Header checkbox toggles only what the filters currently show — filtering is
  // a lens, so an unfiltered row keeps whatever Gina already decided for it.
  function patchAllFiltered(include: boolean) {
    setRows((prev) => {
      const next = new Map(prev)
      for (const it of filtered) {
        const cur = next.get(it.variant_id) ?? { include: true, final_qty: String(it.recommended_qty) }
        next.set(it.variant_id, { ...cur, include })
      }
      return next
    })
  }

  // Tri-state for that header checkbox, read from the same rows map the
  // variant rows read (missing entries default to included).
  const filteredInclude = useMemo(() => {
    let included = 0
    for (const it of filtered) {
      if (rows.get(it.variant_id)?.include ?? true) included++
    }
    return { all: filtered.length > 0 && included === filtered.length, none: included === 0 }
  }, [filtered, rows])

  // Totals cover ALL included rows, not just the filtered view — filtering is
  // a lens, the pull sheet is the whole list.
  const totals = useMemo(() => {
    let itemCount = 0
    let unitCount = 0
    for (const it of recs?.items ?? []) {
      const st = rows.get(it.variant_id)
      if (!st?.include) continue
      const q = finalQtyOf(st)
      if (q >= 1) {
        itemCount++
        unitCount += q
      }
    }
    return { itemCount, unitCount }
  }, [recs, rows])

  function pullSheetItems() {
    return (recs?.items ?? [])
      .filter((it) => {
        const st = rows.get(it.variant_id)
        return st?.include && finalQtyOf(st) >= 1
      })
      .map((it) => ({
        sku: it.sku,
        product_title: it.product_title,
        variant_title: it.variant_title,
        size: it.size,
        product_type: it.product_type,
        final_qty: finalQtyOf(rows.get(it.variant_id)),
      }))
  }

  async function onExport(kind: 'xlsx' | 'html') {
    // Multi-week packs export against the earliest selected week — the
    // pull happens once, before that week starts.
    const target = earliestEvent
    if (!target) return
    const body = { items: pullSheetItems() }
    if (body.items.length === 0) return
    setExporting(kind)
    try {
      const blob = await apiFetchBlob('POST', `/api/events/${target.id}/pullsheet/${kind}`, body)
      const url = URL.createObjectURL(blob)
      if (kind === 'xlsx') {
        const loc = (target.location_name ?? 'event').replace(/[^\w-]+/g, '-')
        const a = document.createElement('a')
        a.href = url
        a.download = `pull-sheet-${loc}-${target.event_date}.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      } else {
        // Printable view — open in a new tab and let the browser print it.
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60_000)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setExporting(null)
    }
  }

  function statusBadges(it: RecommendationItem) {
    const badges: ReactNode[] = []
    if (it.status === 'NEW') badges.push(<Badge key="new" tone="attention">New</Badge>)
    if (it.status === 'CROSS_DISCIPLINE') badges.push(<Badge key="xd">Cross-discipline</Badge>)
    if (!it.stock_ok) badges.push(<Badge key="low" tone="attention">Low stock</Badge>)
    return badges
  }

  // Live column widths: user-dragged overrides from the persisted store,
  // falling back to each column's default. Drag updates the store directly so
  // the <colgroup>, table width, and container cap all follow in real time.
  const widthOf = (c: Column) => packColWidths[c.key] ?? c.width

  function onResizeStart(e: ReactPointerEvent, c: Column) {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startWidth = widthOf(c)
    function onMove(ev: PointerEvent) {
      setPackColWidth(c.key, Math.max(MIN_COL_WIDTH, Math.round(startWidth + ev.clientX - startX)))
    }
    function onUp() {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const selectCls =
    'h-7 border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-xs focus:outline-none focus:border-[var(--brand)]'
  const nothingIncluded = totals.itemCount === 0
  const totalWidth = COLUMNS.reduce((sum, c) => sum + widthOf(c), 0)
  const filterActive = !!(search.trim() || statusFilter || typeFilter || vendorFilter || flagFilter)

  return (
    <div className="dash-page brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      {/* Cap = live table width + p-4 padding + wrap border, so the grid
          fills the container exactly and the whole block centers on wide
          screens — it tracks the user's column resizing. */}
      <div className="flex flex-col flex-1 overflow-hidden p-4 w-full mx-auto" style={{ maxWidth: totalWidth + 34 }}>

        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h1 className="text-base font-semibold">Pack List Builder</h1>
          <div className="flex-1" />
          <EventsSubTabs />
        </div>

        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <select
            className={selectCls}
            value={locationId ?? ''}
            onChange={(e) => onSelectLocation(Number(e.target.value) || undefined)}
            data-testid="packlist-location"
          >
            <option value="">— location —</option>
            {locations.map((l) => (
              <option key={l.location_id} value={l.location_id}>{l.name}</option>
            ))}
          </select>
          <select
            className={selectCls}
            value={eventId ?? ''}
            onChange={(e) => onSelectWeek(Number(e.target.value) || undefined)}
            disabled={locationId == null}
            data-testid="packlist-week"
          >
            <option value="">— event week —</option>
            {locationEvents.map((e) => (
              <option key={e.id} value={e.id}>
                {fmtDate(e.event_date)} · {DISCIPLINE_LABELS[e.discipline] ?? e.discipline}
              </option>
            ))}
          </select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant={extraIds.length > 0 ? 'default' : 'outline'}
                className="h-7 text-xs"
                disabled={eventId == null || locationEvents.length < 2}
                title="Pack one pull for several event weeks — recommendations use history from before the earliest selected week"
              >
                <Layers className="mr-1 h-3 w-3" />
                {extraIds.length > 0 ? `+${extraIds.length} week(s)` : 'Add weeks'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 max-h-[50vh] overflow-y-auto">
              <DropdownMenuLabel className="text-xs font-semibold text-[var(--mute)] uppercase tracking-[0.06em]">
                Pack together with…
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {locationEvents
                .filter((e) => e.id !== eventId)
                .map((e) => (
                  <DropdownMenuCheckboxItem
                    key={e.id}
                    checked={extraIds.includes(e.id)}
                    onCheckedChange={(v) =>
                      setPackSelection({
                        packExtraIds: v
                          ? [...extraIds, e.id]
                          : extraIds.filter((id) => id !== e.id),
                      })
                    }
                    onSelect={(ev) => ev.preventDefault()}
                    className="text-xs"
                  >
                    {fmtDate(e.event_date)} · {DISCIPLINE_LABELS[e.discipline] ?? e.discipline}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <select
            className={selectCls}
            value={discipline}
            onChange={(e) => setPackSelection({ packDiscipline: e.target.value })}
            disabled={eventId == null}
            data-testid="packlist-discipline"
          >
            <option value="">— discipline —</option>
            {Object.entries(DISCIPLINE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {extraIds.length > 0 && (
            <span className="text-xs text-[var(--mute)]">
              Packing {packedEvents.length} weeks · history before {fmtDate(earliestEvent?.event_date)}
            </span>
          )}
          {recs && (
            <span className="text-xs text-[var(--mute)]">
              Generated {recs.generated_at.slice(0, 16).replace('T', ' ')}
            </span>
          )}
          {recs?.at_venue_event_date && (
            <span className="text-xs text-[var(--mute)]">
              At-venue basis: {fmtDate(recs.at_venue_event_date)} event
            </span>
          )}
        </div>

        {selectedEvent == null || !discipline ? (
          <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-8 text-center text-xs text-[var(--mute)]">
            Pick a location and event week to build the pack list. Discipline fills in from the
            registered week — change it to plan a different mix.
          </div>
        ) : isLoading ? (
          <div className="space-y-1">
            {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : !recs || recs.items.length === 0 ? (
          <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-8 text-center text-xs text-[var(--mute)]">
            No recommendations for this location + discipline yet — sync a past event first.
          </div>
        ) : (
          <>
            {/* Filter toolbar — mirrors the Dashboard toolbar */}
            <div className="flex items-center gap-2 mb-0 flex-wrap border border-b-0 border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-1.5">
              <div style={{ position: 'relative', flex: '0 0 240px' }}>
                <Search
                  className="h-3 w-3"
                  style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--mute)', pointerEvents: 'none' }}
                />
                <input
                  type="text"
                  data-testid="packlist-search"
                  placeholder="Search product, SKU, brand…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full h-7 border border-[var(--line-input)] bg-[var(--surface-card)] pl-6 pr-2 text-xs outline-none focus:border-[var(--brand)]"
                />
              </div>
              <select className={selectCls} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select className={selectCls} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All product types</option>
                {facets.types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className={selectCls} value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}>
                <option value="">All brands</option>
                {facets.vendors.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <select className={selectCls} value={flagFilter} onChange={(e) => setFlagFilter(e.target.value)}>
                <option value="">All flags</option>
                <option value="low_stock">Low stock only</option>
                <option value="online_risk">Online risk only</option>
                <option value="stockout">Stocked out last time</option>
              </select>
              {filterActive && (
                <button
                  className="text-xs text-[var(--mute)] underline hover:text-[var(--text-primary)]"
                  onClick={() => {
                    setSearch(''); setStatusFilter(''); setTypeFilter(''); setVendorFilter(''); setFlagFilter('')
                  }}
                >
                  Clear filters
                </button>
              )}
              <div className="flex-1" />
              <span className="text-xs text-[var(--mute)] tabular-nums">
                {sortedGroups.length} product(s) ·{' '}
                {filterActive ? `${filtered.length} of ${recs.items.length}` : `${recs.items.length}`} items
              </span>
            </div>

            <div className="dash-table-wrap border border-[var(--line-warm)]" style={{ minHeight: 0 }}>
              <table className="dash-table" style={{ width: totalWidth }}>
                <colgroup>
                  {COLUMNS.map((c) => <col key={c.key} style={{ width: widthOf(c) }} />)}
                </colgroup>
                <thead>
                  <tr>
                    {COLUMNS.map((c) => {
                      const sorted = sortKey === c.key ? sortDir : false
                      return (
                        <th key={c.key} className="dash-th-sticky" title={c.tooltip}>
                          <div
                            className="dash-th-row"
                            style={
                              c.numeric
                                ? { justifyContent: 'flex-end' }
                                : c.key === 'include'
                                  ? { justifyContent: 'center' }
                                  : undefined
                            }
                          >
                            {c.key === 'include' ? (
                              <input
                                type="checkbox"
                                checked={filteredInclude.all}
                                ref={(el) => {
                                  if (el) el.indeterminate = !filteredInclude.all && !filteredInclude.none
                                }}
                                onChange={(e) => patchAllFiltered(e.target.checked)}
                                aria-label="Include all filtered products"
                              />
                            ) : c.sortable ? (
                              <span
                                className="dash-th-label"
                                onClick={() => onSort(c.key as SortKey)}
                                style={{ cursor: 'pointer', textAlign: c.numeric ? 'right' : 'left' }}
                              >
                                {c.label}
                                <SortIcon sorted={sorted} />
                              </span>
                            ) : (
                              <span className="dash-th-label" style={{ textAlign: c.numeric ? 'right' : 'left' }}>
                                {c.label}
                              </span>
                            )}
                          </div>
                          {c.key !== 'include' && (
                            <div
                              className="dash-th-resize"
                              title="Drag to resize · double-click to reset"
                              onPointerDown={(e) => onResizeStart(e, c)}
                              onDoubleClick={(e) => {
                                e.stopPropagation()
                                resetPackColWidth(c.key)
                              }}
                              onClick={(e) => e.stopPropagation()}
                              role="separator"
                              aria-orientation="vertical"
                              aria-label={`Resize ${c.label || c.key} column`}
                            />
                          )}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortedGroups.length === 0 ? (
                    <tr>
                      <td
                        colSpan={COLUMNS.length}
                        style={{ padding: '40px 16px', textAlign: 'center', fontSize: 13, color: 'var(--mute)' }}
                      >
                        No items match the current filters
                      </td>
                    </tr>
                  ) : (
                    sortedGroups.map((g, idx) => {
                      const dyn = groupDynamics(g)
                      const isOpen = expanded.has(g.key)
                      const rowBg = idx % 2 === 1 ? 'var(--surface-zebra)' : 'var(--surface-card)'
                      const productRow = (
                        <tr
                          key={g.key}
                          className={`dash-tr ${dyn.noneIncluded ? 'opacity-50' : ''}`}
                          style={{ background: rowBg }}
                        >
                          <td className="dash-td" style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={dyn.allIncluded}
                              ref={(el) => {
                                if (el) el.indeterminate = !dyn.allIncluded && !dyn.noneIncluded
                              }}
                              onChange={(e) => patchGroup(g, e.target.checked)}
                              aria-label={`Include all variants of ${g.product_title}`}
                            />
                          </td>
                          <td className="dash-td">
                            <span className="inline-flex gap-1 flex-wrap">
                              {g.statuses.includes('NEW') && <Badge tone="attention">New</Badge>}
                              {g.statuses.includes('CROSS_DISCIPLINE') && <Badge>Cross-discipline</Badge>}
                              {!g.all_stock_ok && <Badge tone="attention">Low stock</Badge>}
                            </span>
                          </td>
                          <td className="dash-td">
                            <Badge>
                              {DISCIPLINE_TAG_LABELS[g.discipline_tag ?? ''] ?? 'All'}
                            </Badge>
                          </td>
                          <td className="dash-td">
                            <button
                              className="inline-flex items-center gap-1 text-left font-semibold hover:text-[var(--text-primary)] max-w-full"
                              onClick={() => toggleExpanded(g.key)}
                            >
                              {isOpen
                                ? <ChevronDown className="h-3 w-3 shrink-0" />
                                : <ChevronRight className="h-3 w-3 shrink-0" />}
                              <span
                                className="truncate"
                                title={g.vendor ? `${g.vendor} · ${g.product_title}` : g.product_title}
                              >
                                {g.vendor && <span className="text-[var(--mute)] font-normal">{g.vendor} · </span>}
                                {g.product_title}
                              </span>
                            </button>
                          </td>
                          <td className="dash-td tabular-nums">{g.items.length}</td>
                          <td className="dash-td tabular-nums">
                            {g.last == null ? (
                              <span className="text-[var(--mute)]">—</span>
                            ) : (
                              <span>
                                {g.last.avail} / {g.last.sold} ·{' '}
                                {g.last.st != null ? `${g.last.st.toFixed(0)}%` : '—'}
                                {g.last.stockout && <span title="A size stocked out — bring more"> ⚠</span>}
                                <span className="text-[var(--mute)]"> ({fmtDate(g.last.date)})</span>
                              </span>
                            )}
                          </td>
                          <td className="dash-td tabular-nums" style={{ textAlign: 'right' }}>
                            {g.at_venue_sum > 0 ? g.at_venue_sum : <span className="text-[var(--mute)]">—</span>}
                          </td>
                          <td className="dash-td tabular-nums" style={{ textAlign: 'right' }}>
                            {g.margin_pct != null ? `${g.margin_pct.toFixed(0)}%` : '—'}
                          </td>
                          <td className="dash-td tabular-nums" style={{ textAlign: 'right' }}>{g.rec_sum}<OfflineQtyPill n={g.offline_sum} /></td>
                          <td className="dash-td tabular-nums" style={{ textAlign: 'right' }}>{g.brewster_sum}</td>
                          <td className="dash-td" style={{ textAlign: 'center' }}>
                            {g.all_stock_ok ? (
                              <span className="text-[var(--status-approved-fg)]" title="Brewster stock covers every variant's recommendation">✓</span>
                            ) : (
                              <span className="text-[var(--status-rejected-fg)]" title="Some variant is short at Brewster — open the product">⚠</span>
                            )}
                          </td>
                          <td className="dash-td" style={{ textAlign: 'center' }}>
                            {dyn.anyRisk ? (
                              <span className="text-[var(--status-awaiting-fg)]" title="A variant's pull would leave the website short — open the product">⚠</span>
                            ) : (
                              <span className="text-[var(--mute)]">—</span>
                            )}
                          </td>
                          <td className="dash-td tabular-nums" style={{ textAlign: 'right' }}>
                            <span title="Sum of included variant quantities — edit inside the product">
                              {dyn.finalSum}
                            </span>
                          </td>
                        </tr>
                      )

                      if (!isOpen) return productRow

                      const variantRows = g.items.map((it) => {
                        const st = rows.get(it.variant_id)
                        const include = st?.include ?? true
                        const onlineRisk = onlineRiskOf(it)
                        return (
                          <tr
                            key={`v-${it.variant_id}`}
                            className={include ? '' : 'opacity-50'}
                            style={{ background: 'var(--surface-page)' }}
                          >
                            <td className="dash-td" style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={include}
                                onChange={(e) => patchRow(it.variant_id, { include: e.target.checked })}
                                aria-label={`Include ${it.product_title ?? it.sku ?? it.variant_id} ${it.variant_title ?? ''}`}
                              />
                            </td>
                            <td className="dash-td">
                              <span className="inline-flex gap-1 flex-wrap">{statusBadges(it)}</span>
                            </td>
                            <td className="dash-td" />
                            <td className="dash-td" style={{ paddingLeft: 28 }}>
                              <span className="text-[var(--mute)]" title={it.rec_reason}>
                                {it.sku ?? ''}
                              </span>
                            </td>
                            <td className="dash-td">{it.variant_title ?? it.size ?? '—'}</td>
                            <td className="dash-td tabular-nums">
                              {it.status === 'NEW' || it.last_event_date == null ? (
                                <span className="text-[var(--mute)]">—</span>
                              ) : (
                                <span title={it.rec_reason}>
                                  {it.last_qty_available ?? it.last_qty_transferred ?? '—'} / {it.last_qty_sold ?? '—'} ·{' '}
                                  {it.last_sell_through_pct != null
                                    ? `${it.last_sell_through_pct.toFixed(0)}%`
                                    : '—'}
                                  {it.last_stockout && <span title="Stocked out — bring more"> ⚠</span>}
                                  <span className="text-[var(--mute)]"> ({fmtDate(it.last_event_date)})</span>
                                </span>
                              )}
                            </td>
                            <td className="dash-td tabular-nums" style={{ textAlign: 'right' }}>
                              {it.est_at_venue > 0 ? it.est_at_venue : <span className="text-[var(--mute)]">—</span>}
                            </td>
                            <td className="dash-td tabular-nums" style={{ textAlign: 'right' }}>
                              {it.margin_pct != null ? `${it.margin_pct.toFixed(0)}%` : '—'}
                            </td>
                            <td className="dash-td tabular-nums" style={{ textAlign: 'right' }}>{it.recommended_qty}<OfflineQtyPill n={it.offline_qty} /></td>
                            <td className="dash-td tabular-nums" style={{ textAlign: 'right' }}>{it.brewster_stock}</td>
                            <td className="dash-td" style={{ textAlign: 'center' }}>
                              {it.stock_ok ? (
                                <span className="text-[var(--status-approved-fg)]" title="Brewster stock covers the recommendation">✓</span>
                              ) : (
                                <span className="text-[var(--status-rejected-fg)]" title="Brewster stock below recommended qty">⚠</span>
                              )}
                            </td>
                            <td className="dash-td" style={{ textAlign: 'center' }}>
                              {onlineRisk ? (
                                <span
                                  className="text-[var(--status-awaiting-fg)]"
                                  title={`Pulling ${finalQtyOf(st)} leaves ${it.brewster_stock - finalQtyOf(st)} — under 2 weeks of online sales (${it.online_weekly_velocity}/wk)`}
                                >
                                  ⚠
                                </span>
                              ) : (
                                <span className="text-[var(--mute)]">—</span>
                              )}
                            </td>
                            <td className="dash-td" style={{ textAlign: 'right' }}>
                              <input
                                type="number"
                                min={0}
                                value={st?.final_qty ?? String(it.recommended_qty)}
                                onChange={(e) => patchRow(it.variant_id, { final_qty: e.target.value })}
                                disabled={!include}
                                className="w-16 h-6 border border-[var(--line-input)] bg-[var(--surface-card)] px-1 text-right text-xs tabular-nums outline-none focus:border-[var(--brand)] disabled:opacity-40"
                                aria-label={`Final qty for ${it.product_title ?? it.sku ?? it.variant_id} ${it.variant_title ?? ''}`}
                              />
                            </td>
                          </tr>
                        )
                      })

                      const curveRow = g.size_curve.length > 0 && (
                        <tr key={`${g.key}-curve`} style={{ background: 'var(--surface-page)' }}>
                          <td className="dash-td" colSpan={COLUMNS.length} style={{ whiteSpace: 'normal', paddingLeft: 28 }}>
                            <div className="text-xs py-1">
                              <span className="font-semibold mr-3">Size curve — this location + discipline:</span>
                              {g.size_curve.map((s) => (
                                <span key={s.size} className="inline-flex items-center gap-1 tabular-nums mr-4">
                                  <strong>{s.size}</strong>
                                  {s.sell_through_pct != null ? (
                                    <span
                                      className={SELL_THROUGH_TEXT[sellThroughLevel(s.sell_through_pct)]}
                                      data-testid="size-curve-st"
                                    >
                                      {s.sell_through_pct.toFixed(0)}%
                                    </span>
                                  ) : '—'}
                                  <span className="text-[var(--mute)]">({s.qty_sold}/{s.qty_available})</span>
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )

                      return [productRow, curveRow, ...variantRows]
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-3 border border-t-0 border-[var(--line-warm)] bg-[var(--surface-card)] px-3 py-2 text-xs shrink-0">
              <span>
                <strong className="tabular-nums">{totals.itemCount}</strong> item(s) ·{' '}
                <strong className="tabular-nums">{totals.unitCount}</strong> units to pull
                {filterActive && <span className="text-[var(--mute)]"> (totals include items hidden by filters)</span>}
              </span>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={nothingIncluded || exporting != null}
                onClick={() => onExport('xlsx')}
                data-testid="packlist-export-xlsx"
              >
                <FileSpreadsheet className="mr-1 h-3 w-3" />
                {exporting === 'xlsx' ? 'Exporting…' : 'Excel pull sheet'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={nothingIncluded || exporting != null}
                onClick={() => onExport('html')}
                data-testid="packlist-export-html"
              >
                <Printer className="mr-1 h-3 w-3" />
                {exporting === 'html' ? 'Opening…' : 'Print view'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
